import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  heightForLabels,
  layoutCategoryLabels,
  layoutLegend,
  type Rect,
  TICK_GAP,
  TICK_SIZE,
} from "@/lib/tool-kit/charts/layout";
import { linePath } from "@/lib/tool-kit/charts/lines";
import {
  bandScale,
  linearScale,
  type Scale,
  stepDecimals,
} from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import type { ClimateAxes } from "@/lib/tool-kit/earth-science/climate";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A climate graph: precipitation bars on the right axis, temperature line on the left.
 * Both axes share gridlines and the zero line (see `climateAxes`), so a bar and the line
 * above it can be read against the same horizontal rules.
 */

export interface ClimateChartMonth {
  label: string;
  temperature: number;
  precipitation: number;
}

/** Room for the right axis title, beyond its tick labels. */
const RIGHT_TITLE_BAND = CHART_FONT_SIZE.axisTitle * 1.25 + 8;
const BAND_PADDING = { paddingInner: 0.3, paddingOuter: 0.15 };
const BAR_MAX = 44;

function range(min: number, max: number, step: number): number[] {
  const count = Math.round((max - min) / step);
  const decimals = stepDecimals(step);
  return Array.from(
    { length: count + 1 },
    (_, index) => Number((min + index * step).toFixed(decimals)) || 0
  );
}

/** The right-hand value axis: ticks and labels for the part at or above zero. */
function RightAxis({
  plot,
  scale,
  ticks,
  format,
  apa,
}: {
  plot: Rect;
  scale: Scale;
  ticks: readonly number[];
  format: (value: number) => string;
  apa: boolean;
}) {
  const ink = apa ? CHART_INK.text : CHART_INK.axis;
  const label = apa ? CHART_INK.text : CHART_INK.muted;
  const right = plot.x + plot.width;
  return (
    <g>
      <line
        stroke={ink}
        strokeWidth={1}
        x1={px(right)}
        x2={px(right)}
        y1={px(plot.y)}
        y2={px(plot.y + plot.height)}
      />
      {ticks
        .filter((tick) => tick >= 0)
        .map((tick) => {
          const y = px(scale(tick));
          return (
            <g key={tick}>
              <line
                stroke={ink}
                strokeWidth={1}
                x1={px(right)}
                x2={px(right + TICK_SIZE)}
                y1={y}
                y2={y}
              />
              <text
                fill={label}
                fontSize={CHART_FONT_SIZE.tick}
                textAnchor="start"
                x={px(right + TICK_SIZE + TICK_GAP)}
                y={px(y + CHART_FONT_SIZE.tick * 0.35)}
              >
                {format(tick)}
              </text>
            </g>
          );
        })}
    </g>
  );
}

export function ClimateChart({
  id,
  months,
  axes,
  tempTitle,
  precipTitle,
  style,
  fmt,
  label,
  description,
}: {
  id: string;
  months: ClimateChartMonth[];
  axes: ClimateAxes;
  /** Left axis title, e.g. "Temperature (°C)"; also the line's legend entry. */
  tempTitle: string;
  /** Right axis title, e.g. "Precipitation (mm)"; also the bars' legend entry. */
  precipTitle: string;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const tempTicks = range(axes.tempMin, axes.tempMax, axes.tempStep);
  const precipTicks = range(axes.precipMin, axes.precipMax, axes.precipStep);
  const tempFormat = tickFormat(fmt, stepDecimals(axes.tempStep));
  const precipFormat = tickFormat(fmt, stepDecimals(axes.precipStep));
  const legend = layoutLegend(
    [precipTitle, tempTitle],
    CHART_WIDTH - 2 * CHART_PADDING
  );
  const rightLabelWidth = maxTextWidth(
    precipTicks.filter((tick) => tick >= 0).map(precipFormat),
    { fontSize: CHART_FONT_SIZE.tick }
  );
  const base = {
    width: CHART_WIDTH,
    title: style.apa ? "" : style.title,
    yTitle: tempTitle,
    legendHeight: legend.height,
    leftLabelWidth: maxTextWidth(tempTicks.map(tempFormat), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    rightPadding: rightLabelWidth + TICK_SIZE + TICK_GAP + RIGHT_TITLE_BAND,
    topPadding: style.valueLabels ? 18 : 10,
  };
  const probe = cartesianFrame({
    ...base,
    height: CHART_HEIGHT,
    bottomLabelHeight: 0,
  });
  const band = bandScale(
    months.length,
    [probe.plot.x, probe.plot.x + probe.plot.width],
    BAND_PADDING
  );
  const labels = layoutCategoryLabels(
    months.map((month) => month.label),
    band.step
  );
  const frame = cartesianFrame({
    ...base,
    height: heightForLabels(labels.height),
    bottomLabelHeight: labels.height,
  });
  const { plot } = frame;
  const bandAt = bandScale(
    months.length,
    [plot.x, plot.x + plot.width],
    BAND_PADDING
  );
  const yRange: [number, number] = [plot.y + plot.height, plot.y];
  const yTemp = linearScale([axes.tempMin, axes.tempMax], yRange);
  const yPrecip = linearScale([axes.precipMin, axes.precipMax], yRange);
  const zero = yTemp(0);
  const barColor = paletteColor(colorIndex(style.color), 0);
  const start = colorIndex(style.color) === 3 ? 0 : 3;
  const lineColor = paletteColor(start, 0);
  const barWidth = Math.min(BAR_MAX, bandAt.bandwidth);
  const points = months.map(
    (month, index) => [bandAt.center(index), yTemp(month.temperature)] as const
  );
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      <SvgLegend
        keys={[
          { color: barColor, kind: "swatch" },
          { color: lineColor, kind: "line", shape: "circle" },
        ]}
        layout={legend}
        x={CHART_PADDING}
        y={frame.legendTop}
      />
      <ValueAxis
        apa={style.apa}
        format={tempFormat}
        orient="left"
        plot={plot}
        scale={yTemp}
        ticks={tempTicks}
        zeroLine
      />
      <RightAxis
        apa={style.apa}
        format={precipFormat}
        plot={plot}
        scale={yPrecip}
        ticks={precipTicks}
      />
      <text
        fill={CHART_INK.text}
        fontSize={CHART_FONT_SIZE.axisTitle}
        textAnchor="middle"
        transform={`rotate(90 ${px(frame.width - CHART_PADDING - 6)} ${px(plot.y + plot.height / 2)})`}
        x={px(frame.width - CHART_PADDING - 6)}
        y={px(plot.y + plot.height / 2)}
      >
        {precipTitle}
      </text>
      <CategoryAxis
        apa={style.apa}
        axisLine
        labels={labels}
        orient="bottom"
        plot={plot}
        positions={months.map((_, index) => bandAt.center(index))}
        ticks
      />
      {months.map((month, index) => {
        const top = yPrecip(month.precipitation);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: one bar per month, in order
          <g key={index}>
            <rect
              fill={barColor}
              height={px(Math.max(0, zero - top))}
              width={px(barWidth)}
              x={px(bandAt.center(index) - barWidth / 2)}
              y={px(top)}
            />
            {style.valueLabels ? (
              <text
                fill={CHART_INK.text}
                fontSize={CHART_FONT_SIZE.value}
                textAnchor="middle"
                x={px(bandAt.center(index))}
                y={px(top - 4)}
              >
                {valueText(fmt, month.precipitation, 1)}
              </text>
            ) : null}
          </g>
        );
      })}
      <path
        d={linePath(points)}
        fill="none"
        stroke={lineColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
      />
      {months.map((month, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: one marker per month, in order
        <g key={index}>
          <Marker
            fill={lineColor}
            shape="circle"
            x={points[index][0]}
            y={points[index][1]}
          />
          {style.valueLabels ? (
            <text
              fill={CHART_INK.text}
              fontSize={CHART_FONT_SIZE.value}
              fontWeight="bold"
              textAnchor="middle"
              x={px(points[index][0])}
              y={px(points[index][1] - 9)}
            >
              {valueText(fmt, month.temperature, 1)}
            </text>
          ) : null}
        </g>
      ))}
    </ChartSvg>
  );
}
